import Table from 'common/table/table';
import {
  ThemeHeader,
} from 'common/table/table-helper';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Div, Dropdown } from 'ui';
import { getReferenceData } from '../../../common/reference-data/containers/reference-data-actions';
import { referenceTypes } from '../../../utils/app-configs';

const Wrapper = styled(Div)`
  box-shadow: rgba(0, 0, 0, 0.27) 0px;  
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const StyledDropDown = styled(Dropdown)`
  margin: 10px 0 10px;
`;

const Characteristics = ({
  characteristics = [],
  intl,
  onChange,
  isEditable,
  pageSize = 10,
  setFormDirty,
  disabled,
}) => {
  const dispatch = useDispatch();
  const { all_equipment_equipmentCharacteristics: editableCharacteristics } =
    useSelector(state => state.referenceData.data);

  useEffect(() => {
    if (isEditable) {
      dispatch(getReferenceData({
        objectType: referenceTypes.equipment,
        name: 'equipmentCharacteristics',
        serviceCenterId: 'all',
      }));
    }
  }, [isEditable]);

  const handleValueChange = (id, value) => {
    const newValues = cloneDeep(characteristics);
    const targetObject = newValues?.find(v => v.id === id);
    if (targetObject) {
      targetObject.value = value.value;
      targetObject.valueDescription = value.description;
    }
    onChange(newValues);
    setFormDirty();
  };

  const columns = [
    {
      id: 'name',
      displayName: intl.formatMessage({ id: 'equipment.characteristics.CHARACTERISTIC' }),
      fieldName: 'name',
      tooltip: row => row.name,
      width: '235px',
    },
    {
      id: 'value',
      displayName: intl.formatMessage({ id: 'equipment.characteristics.VALUE' }),
      fieldName: 'value',
      renderFn: (value) => {
        const options = editableCharacteristics?.find(q => q.number === value.number)?.values;
        const targetOption = characteristics?.find(o => o?.value === value.value);
        return isEditable && isToggleEnabled('canEditEquipmentCharacteristics') && options?.length ?
          <StyledDropDown
            width={'100%'}
            values={options}
            disabled={disabled}
            value={`${targetOption?.valueDescription} (${targetOption?.value})`}
            valueKey='value'
            valueLabel='description'
            getPopupContainer={() => document.body}
            onChange={e => handleValueChange(value.id, options.find(o => o.value === e))}
          />
          :
          // eslint-disable-next-line max-len
          `${value.valueDescription || intl.formatMessage({ id: 'equipment.characteristics.NO_DESCRIPTION' })}${value.value ? ` (${value.value})` : ''}`;
      },
    },
  ];

  const renderHeader = () => <>
    <ThemeHeader inlineTable={true}>
      <FormattedMessage id='equipment.characteristics.CHARACTERISTICS' />
      {' '}({characteristics?.length || 0})
    </ThemeHeader>
  </>;

  return (
    <Wrapper data-test-id='characteristics'>
      <Table
        renderCustomHeader={renderHeader}
        hasCustomHeader={true}
        labelInColumn={false}
        columns={columns}
        data={characteristics?.slice(0, pageSize) || []}
        fullWidth={true}
        lightHeader={true}
      />
    </Wrapper>
  );
};

Characteristics.propTypes = {
  characteristics: PropTypes.array,
  intl: PropTypes.object.isRequired,
  isEditable: PropTypes.bool,
  pageSize: PropTypes.number,
  setFormDirty: PropTypes.func,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default injectIntl(Characteristics);
